import React, {useEffect} from "react";
import zoomSdk from "@zoom/appssdk";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";

import Box from "@amzn/meridian/esm/components/box";
import Column from "@amzn/meridian/esm/components/column";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Icon from "@amzn/meridian/esm/components/icon";
import Link from "@amzn/meridian/esm/components/link";
import Theme from "@amzn/meridian/esm/components/theme";

import alertFilledTokens from "@amzn/meridian-tokens/base/icon/alert-knockout";
import errorKnockout from "@amzn/meridian-tokens/base/icon/error-knockout";
import lineIcon from "../../../images/line-summary-sections.svg";
import LogManager from "../../utils/LogManager";
import {SUMMARY_CLASSIFICATION_TYPE} from "./constants";

/**
 * SummaryBodyInDetailPage Component
 *
 * Renders the detailed body content of a meeting summary in the summary detail page.
 * This component encapsulates all conditional rendering logic based on `summary.summaryType`,
 * such as displaying warnings or error messages, or the full summary content including:
 * - Narrative summary
 * - Decisions (as bullet points)
 * - Next Steps (as bullet points)
 * - A footer note about AI-generated content
 *
 * Special Cases Handled:
 * - "attorney_in_attendance": show "Attorney in attendance" warning, the rest of the summary info is the same as a normal summary
 * - "server_error": Display server issue notice
 * - "short_meeting": Show warning for meetings too short to summarize
 * - "not_enough_audio": Show warning about missing conversation audio
 * - Meeting topics not approved: Link to internal documentation
 *
 * Props:
 * - `summary` (object): Required. Meeting summary metadata, narrative, decisions, and next steps.
 * - isTopicNotApproved (boolean): Indicates if the meeting topic is not approved
 */
const SummaryBodyInDetailPage = ({ summary, isTopicNotApproved }) => {

  // Message for special cases like attorney_in_attendance, not_enough_transcript etc
  const SummaryMessage = ({ icon, color, children }) => (
    <Row width="100%" widths={["16px", "8px", "fill"]} wrap="none" alignmentHorizontal="start" spacing="none" alignmentVertical="top">
      <Icon tokens={icon} color={color} />
      <Row width="8px" />
      <Box>
        <Text type="b100" fontFamily="amazonEmber">
          {children}
        </Text>
      </Box>
    </Row>
  );

  // Function to render individual sections from structured summary
  const renderSection = (section, index) => {
    const { type, format, content, items, title } = section;
    
    // Log section processing for troubleshooting
    LogManager.info(`renderSection: Processing section ${index} - type="${type}", format="${format}"`);
    
    switch (format) {
      case "text":
        LogManager.info(`renderSection: Rendering text format`);
        return (
          <Box key={`section-${type}-${index}`}>
            <Text type="b100" fontFamily="amazonEmber">
              {content}
            </Text>
          </Box>
        );
        
      case "markdown":
        LogManager.info(`renderSection: Rendering markdown format`);
        return (
          <Box key={`section-${type}-${index}`}>
            <Text type="b100" fontFamily="amazonEmber">
              <ReactMarkdown remarkPlugins={[remarkBreaks]}>
                {content?.replace(/\n/gi, "&nbsp; \n")}
              </ReactMarkdown>
            </Text>
          </Box>
        );
        
      case "list":
        LogManager.info(`renderSection: Rendering list format with ${items?.length || 0} items`);
        return (
          <React.Fragment key={`section-${type}-${index}`}>
            <Box spacing="none" spacingInset="none">
              <Column spacing="none" spacingInset="none">
                {title && (
                  <>
                    <Row spacing="none" spacingInset="none">
                      <Row width="8px">
                        <img src={lineIcon} alt="lineIcon" style={{ display: "block", margin: "auto", maxWidth: "100%", height: "auto" }} />
                      </Row>
                      <Text type="h50" fontFamily="amazonEmber">
                        {title}
                      </Text>
                    </Row>
                    <Column height="16px" />
                  </>
                )}
                <Box spacing="none" spacingInset="none">
                  <ul style={{ paddingLeft: "20px", margin: 0 }}>
                    {items?.map((item, itemIndex) => {
                      const parts = item.split(/\\n|\n/);
                      return (
                        <li key={itemIndex}>
                          {parts.map((part, partIndex) => (
                            <Text
                              key={partIndex}
                              type="b100"
                              fontFamily="amazonEmber"
                              display="block"
                            >
                              {part}
                            </Text>
                          ))}
                        </li>
                      );
                    })}
                  </ul>
                </Box>
              </Column>
            </Box>
          </React.Fragment>
        );
        
      default:
        LogManager.warn(`Unknown section format: ${format} for type: ${type}`);
        return null;
    }
  };

  // Function to render structured sections with proper spacing
  const renderStructuredSections = (structuredSummary) => {
    const { sections } = structuredSummary;
    if (!sections || sections.length === 0) {
      return null;
    }

    const elements = [];
    
    sections.forEach((section, index) => {
      const renderedSection = renderSection(section, index);

      if (renderedSection) {
        // Add spacing between sections
        if (index > 0) {
          if (section?.format === "list") {
            elements.push(<Column key={`spacing-${index}`} height="24px" />);
          } else {
            elements.push(<Column key={`spacing-${index}`} height="16px" />);
          }
        }
        elements.push(renderedSection);
      }
    });

    return elements;
  };

  const handleLearnMoreClick = async () => {
    try {
      await zoomSdk.openUrl({ url: "https://w.amazon.com/bin/view/UnifiedCommunications/MeetingSummaries/AiMS" });
    } catch (error) {
      LogManager.error("Failed to open URL in Zoom:", error);
    }
  };

  const isAttorneyInAttendance = summary.summaryType === SUMMARY_CLASSIFICATION_TYPE.ATTORNEY_IN_ATTENDANCE;

  // Early return for error/warning cases (no organizer message)
  if (!summary.organizerWarningMessage && summary.iconType && !isAttorneyInAttendance) {
    return (
      <Column spacing="none">
        <SummaryMessage icon={summary.iconType === "warning" ? alertFilledTokens : errorKnockout} color={summary.iconType}>
          {summary.summaryHighlight}
        </SummaryMessage>
        {isTopicNotApproved && (
          <>
            <Column height="16px" />
            <Link onClick={handleLearnMoreClick} type="secondary">
              <Theme tokens={{ textColorPrimary: "#006D5B" }}>
                <Text color="primary" type="b200">
                  <u>Learn more</u>
                </Text>
              </Theme>
            </Link>
          </>
        )}
      </Column>
    );
  }

  return (
    <>
      {isAttorneyInAttendance && (
        <>
          <Column height="8px" />
          <Column spacing="none">
            <SummaryMessage icon={alertFilledTokens} color="warning">
              Attorney in attendance
            </SummaryMessage>
          </Column>
          <Column height="24px" />
        </>
      )}
      {/* Organizer Warning Message - show and continue */}
      {summary.organizerWarningMessage && (
        <>
          <Column spacing="none">
            <SummaryMessage icon={alertFilledTokens} color="warning">
              {summary.organizerWarningMessage}
            </SummaryMessage>
          </Column>
          <Column height="24px" />
        </>
      )}

      {/* Render structured summary */}
      {summary.structuredSummary?.sections?.length > 0 && (
        <>
          {renderStructuredSections(summary.structuredSummary)}
        </>
      )}

      {/* AIMS Footer - only show if there's any content */}
      {summary.structuredSummary?.sections?.length > 0 && (
        <>
          <Column height="25px" />
          <Column spacing="none" spacingInset="none">
            <Theme tokens={{ textColorPrimary: "#575F67" }}>
              <Text fontFamily="amazonEmber" type="b100" color="primary">
                <b>Amazon Meetings Summary</b> is powered by Amazon AI. Always check for accuracy.
              </Text>
            </Theme>
          </Column>
        </>
      )}
    </>
  );
};

export default SummaryBodyInDetailPage;
